import { UserGroup, UserInfo, UserOrganization } from '@contexts/users/types';
import { hasSalesGroup } from '@contexts/users/sales-groups';

export const ORGANIZATIONS_CLAIM = 'https://clarke.com.br/organizations';

export const LEGACY_ORGANIZATION_ID_CLAIM = 'org_id';

export const LEGACY_ORGANIZATION_CLAIM = 'https://clarke.com.br/organization';

const ORGANIZATION_ID_PATTERN = /^org_[A-Za-z0-9]+$/;
const ORGANIZATION_ID_MAX_LENGTH = 64;

type ClaimRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is ClaimRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isOrganizationId = (value: unknown): value is string =>
  typeof value === 'string' && value.length <= ORGANIZATION_ID_MAX_LENGTH && ORGANIZATION_ID_PATTERN.test(value);

const nameOr = (name: unknown, fallback: string): string =>
  typeof name === 'string' && name.trim() ? name.trim() : fallback;

const listedOrganizations = (claim: unknown): UserOrganization[] =>
  (Array.isArray(claim) ? claim : []).flatMap((entry: unknown) =>
    isRecord(entry) && isOrganizationId(entry.id) ? [{ id: entry.id, name: nameOr(entry.name, entry.id) }] : [],
  );

const legacyOrganization = (userData: ClaimRecord): UserOrganization[] => {
  const id = userData[LEGACY_ORGANIZATION_ID_CLAIM];
  if (!isOrganizationId(id)) return [];
  const legacy = userData[LEGACY_ORGANIZATION_CLAIM];
  const sameOrganization = isRecord(legacy) && (legacy.id === undefined || legacy.id === id);
  return [{ id, name: sameOrganization ? nameOr(legacy.display_name, nameOr(legacy.name, id)) : id }];
};

export const parseOrganizations = (userData: unknown): UserOrganization[] => {
  if (!isRecord(userData)) return [];
  const seen = new Set<string>();
  return [...listedOrganizations(userData[ORGANIZATIONS_CLAIM]), ...legacyOrganization(userData)].filter(({ id }) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const parseUserInfo = (userData: any): UserInfo | undefined => {
  if (!userData) return undefined;

  const userGroups = userData['https://clarke.com.br/groups'] as UserGroup[];

  return {
    id: userData['https://clarke.com.br/uuid'],
    name: userData.name,
    email: userData.email,
    pictureUrl: userData.picture,
    isSuperAdmin: hasSalesGroup(userGroups, 'superAdmin'),
    isAdmin: hasSalesGroup(userGroups, 'admin'),
    roles: userData['https://clarke.com.br/roles'] ?? [],
    groups: userGroups ?? [],
    phone: userData.phone_number,
    organizations: parseOrganizations(userData),
  };
};

export const buildServerBaseURL = (
  protocol: string,
  host: string,
  serverPort: string,
  isProduction: boolean,
): string => {
  const currentDomain = `${protocol}//${host}`;
  const portSuffix = isProduction ? '' : `:${serverPort}`;
  return `${currentDomain.replace(/:\d+/, portSuffix)}/api/auth`;
};

export const isInsufficientPermissionsError = (error: any): boolean => {
  return error?.response?.data?.message === 'Usuário não possui permissões mínimas para acessar o sistema.';
};
